import { wordHelp } from '../lib/pronunciation.ts';
import type { Player } from '../speech/audio.ts';
import { audioIds } from '../speech/audioIds.ts';
import type { TeacherLang, WordScore } from '../types.ts';
import type { AppCtx } from './ctx.ts';
import { button, h } from './dom.ts';
import { tx, txAttr } from './i18n.ts';
import { icon } from './kit.ts';
import { L } from './taskText.ts';

const T = {
  say: { en: 'Say it like: ', pl: 'Czytaj: ' },
  approx: { en: ' (in Polish letters, not exact)', pl: ' (zapis przybliżony)' },
  meaning: { en: 'Meaning: ', pl: 'Znaczenie: ' },
  locked: { en: '🎤 The sound will turn on when you finish speaking.', pl: '🎤 Dźwięk włączy się, gdy skończysz mówić.' },
  tipsTitle: { en: 'Pronunciation tips:', pl: 'Wskazówki do wymowy:' },
  weakSound: (ipa: string) => ({ en: `The weakest sound was “${ipa}”. `, pl: `Najsłabiej wyszło „${ipa}”. ` }),
  weakEnding: { en: 'The weakest part was the ending. ', pl: 'Najsłabiej wyszła końcówka. ' },
  soundIs: (ipa: string) => ({ en: `“${ipa}” `, pl: `„${ipa}” ` }),
  howToRead: (word: string) => ({ en: `How to read: ${word}`, pl: `Jak czytać: ${word}` }),
};

/**
 * Karta „jak to się czyta” dla jednego słowa: zapis fonetyczny, wymowa „po polsku”, odsłuch (normalny i wolny) i rady do trudnych głosek.
 * `scored` to ocena tego słowa z ostatniej próby — wtedy pierwsza rada dotyczy najsłabszej głoski.
 * `opts.showReading=false` — bez linijki „Czytaj: …” i `opts.listenIcon` — ikona głośnika na „Posłuchaj” i żółwia na „Wolno” (karta nowego słowa, wg makiety).
 * `soundLocked` — trwa nagrywanie: kartę widać i można ją czytać, ale odsłuch jest wyłączony (dźwięk z głośnika wpadłby do mikrofonu i zepsuł ocenę).
 */
export interface WordHelpOptions {
  showReading?: boolean;
  listenIcon?: boolean;
}

/**
 * Zamykanie karty „jak to się czyta” bez celowania w ✕ (patch-40): dotknięcie DOWOLNEGO miejsca poza kartą zamyka ją, tak samo klawisz Esc.
 * `keep` to elementy, których dotknięcie karty NIE zamyka: sama karta, słowo, które ją otwiera (dotknięcie go odświeża kartę), a na ekranie
 * mówienia także pasek z mikrofonem — dziecko czyta radę z karty i od razu naciska mikrofon (albo „Spróbuj jeszcze raz”), więc karta musi zostać.
 * Karty nie zamyka też przełącznik języka EN/PL (oznaczony `data-keeps-help`): zmiana języka przepisuje napisy w miejscu, a karta ma zostać — po to ją
 * czyta się właśnie po polsku. Nasłuch jest na całym dokumencie, bo „poza kartą” to cała reszta ekranu, nie tylko jego kontener. Dotknięcie rozpoznajemy po `pointerup`
 * (iPad/Safari nie wysyła `click` do dokumentu dla elementów, które same nie są klikalne), a przeciągnięcie palcem (przewijanie, zaznaczanie)
 * go nie zamyka: liczy się tylko ruch krótszy niż TAP_SLOP px, który zaczął się i skończył poza kartą. `click` zostaje jako zapas
 * (klawiatura, czytniki ekranu). Zwraca funkcję zdejmującą nasłuchy — widok woła ją w `destroy`.
 */
const TAP_SLOP = 10;
/** Atrybut elementu, którego dotknięcie (albo dotknięcie czegokolwiek w nim) nie zamyka karty. */
export const KEEPS_HELP = 'data-keeps-help';

export interface OutsideTapOptions {
  isOpen: () => boolean;
  keep: () => ReadonlyArray<Element | null>;
  close: () => void;
}

export function dismissOnOutsideTap(o: OutsideTapOptions): () => void {
  // `composedPath()` jest policzona w chwili zdarzenia, więc działa też, gdy obsługa kliknięcia wymieniła już element (np. odrysowanie karty)
  const inside = (e: Event): boolean => {
    const path = typeof e.composedPath === 'function' ? e.composedPath() : [];
    const target = e.target as Node | null;
    const marked = path.some((n) => typeof (n as Element).getAttribute === 'function' && (n as Element).getAttribute(KEEPS_HELP) !== null);
    return marked || o.keep().some((k) => k !== null && (path.includes(k) || (target !== null && k.contains(target))));
  };
  let down: { x: number; y: number } | null = null; // początek dotknięcia, które zaczęło się poza kartą przy otwartej karcie
  const onDown = (e: Event) => {
    const p = e as PointerEvent;
    down = o.isOpen() && !inside(e) ? { x: p.clientX, y: p.clientY } : null;
  };
  const onUp = (e: Event) => {
    const start = down;
    down = null;
    if (!start || !o.isOpen() || inside(e)) return;
    const p = e as PointerEvent;
    if (Math.hypot(p.clientX - start.x, p.clientY - start.y) <= TAP_SLOP) o.close();
  };
  const onCancel = () => { down = null; }; // przeglądarka przejęła gest (przewijanie) — to nie było dotknięcie
  const onClick = (e: Event) => {
    if (o.isOpen() && !inside(e)) o.close();
  };
  const onKey = (e: Event) => {
    if ((e as KeyboardEvent).key === 'Escape' && o.isOpen()) o.close();
  };
  document.addEventListener('pointerdown', onDown);
  document.addEventListener('pointerup', onUp);
  document.addEventListener('pointercancel', onCancel);
  document.addEventListener('click', onClick);
  document.addEventListener('keydown', onKey);
  return () => {
    document.removeEventListener('pointerdown', onDown);
    document.removeEventListener('pointerup', onUp);
    document.removeEventListener('pointercancel', onCancel);
    document.removeEventListener('click', onClick);
    document.removeEventListener('keydown', onKey);
  };
}

export function renderWordHelp(ctx: Pick<AppCtx, 'teacher' | 'account'>, word: string, scored: WordScore | null, player: Player, onClose: () => void, soundLocked = false, meaning: string | null = null, opts: WordHelpOptions = {}): HTMLElement {
  const { showReading = true, listenIcon = false } = opts;
  const help = wordHelp(word, scored);
  // napisy karty idą za przełącznikiem EN/PL (tx / txAttr przepisują je w miejscu, więc stan karty — otwarta, zablokowany dźwięk — zostaje)
  const reading = (lang: TeacherLang): string => (lang === 'en' ? help.plEn : help.pl) ?? '';
  const say = (slow: boolean) => {
    if (soundLocked) return Promise.resolve();
    const id = slow ? audioIds.wordSlow(help.word) : audioIds.word(help.word);
    return player.play(id, { text: help.word, lang: 'en', slow });
  };
  const lines = [
    showReading && help.pl ? h('p', { class: 'wh-pl', 'data-testid': 'wh-pl' }, tx(ctx, T.say), h('strong', null, tx(ctx, reading)), h('span', { class: 'muted' }, tx(ctx, T.approx))) : null,
    meaning ? h('p', { class: 'wh-mean', 'data-testid': 'wh-mean' }, tx(ctx, T.meaning), h('strong', null, meaning)) : null,
  ];
  const closeBtn = txAttr(ctx, button(icon('close'), onClose, { class: 'icon-btn small', testid: 'wh-close' }), 'title', L.close);
  return txAttr(ctx, h(
    'div',
    { class: 'word-help', 'data-testid': 'word-help', 'data-word': help.word, role: 'region' },
    h(
      'div',
      { class: 'wh-head' },
      h('strong', { class: 'wh-word' }, help.word),
      help.ipa ? h('span', { class: 'wh-ipa', 'data-testid': 'wh-ipa' }, `/${help.ipa}/`) : h('span', { class: 'wh-ipa' }),
      closeBtn,
    ),
    h('div', { class: 'wh-lines' }, ...lines),
    h(
      'div',
      { class: 'wh-actions' },
      button(listenIcon ? [icon('speaker'), tx(ctx, L.listen)] : tx(ctx, L.listen), () => void say(false), { class: 'btn ghost', testid: 'wh-listen', disabled: soundLocked }),
      button(listenIcon ? [icon('turtle'), tx(ctx, L.slowly)] : tx(ctx, L.slowly), () => void say(true), { class: 'btn ghost', testid: 'wh-slow', disabled: soundLocked }),
    ),
    soundLocked ? h('p', { class: 'wh-locked muted', 'data-testid': 'wh-locked' }, tx(ctx, T.locked)) : null,
    help.tips.length > 0 ? h('p', { class: 'wh-tips-title', 'data-testid': 'wh-tips-title' }, tx(ctx, T.tipsTitle)) : null,
    help.tips.length > 0
      ? h(
          'ul',
          { class: 'wh-tips' },
          ...help.tips.map((t) =>
            h('li', { 'data-testid': 'wh-tip', 'data-kind': t.kind }, t.kind === 'weak' ? h('strong', null, tx(ctx, t.ipa ? T.weakSound(t.ipa) : T.weakEnding)) : t.ipa ? h('strong', null, tx(ctx, T.soundIs(t.ipa))) : null, tx(ctx, { en: t.textEn, pl: t.text })),
          ),
        )
      : null,
  ), 'aria-label', T.howToRead(help.word));
}
